import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Copy,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-hot-toast";

import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  fetchAdminOrderById,
  isAdminOrderNotFound,
} from "@/services/admin/orders.api";
import type { AdminOrderDetail } from "@/services/admin/orders.types";

import {
  formatOrderCurrency,
  formatOrderDateTime,
  getEntitlementStateLabel,
  getEntitlementStateVariant,
  getOrderStatusLabel,
  getOrderStatusVariant,
  getPaymentMethodLabel,
  getPaymentProviderLabel,
  getPaymentStatusLabel,
  getPaymentStatusVariant,
} from "./orderLabels";

const DetailSkeleton: React.FC = () => (
  <div className="space-y-4" aria-busy="true" aria-label="Carregando detalhes do pedido">
    <div className="h-10 w-2/3 rounded-md bg-gray-100 animate-pulse" />
    <div className="h-32 rounded-lg bg-gray-100 animate-pulse" />
    <div className="h-32 rounded-lg bg-gray-100 animate-pulse" />
    <div className="h-48 rounded-lg bg-gray-100 animate-pulse" />
  </div>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    {children}
  </section>
);

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, value }) => (
  <div>
    <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{value}</dd>
  </div>
);

const AdminOrderDetailPage: React.FC = () => {
  const { orderId = "" } = useParams<{ orderId: string }>();
  const location = useLocation();
  const ledgerSearch =
    (location.state as { ledgerSearch?: string } | null)?.ledgerSearch ?? "";

  const backHref = ledgerSearch ? `/admin/orders?${ledgerSearch}` : "/admin/orders";

  const [order, setOrder] = useState<AdminOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  const loadOrder = useCallback(async () => {
    if (!orderId) return;

    setLoading(true);
    setError(null);
    setNotFound(false);

    try {
      const detail = await fetchAdminOrderById(orderId);
      setOrder(detail);
    } catch (err) {
      if (isAdminOrderNotFound(err)) {
        setNotFound(true);
        setOrder(null);
      } else {
        console.error("Failed to load admin order detail:", err);
        setError("Não foi possível carregar o pedido.");
        setOrder(null);
      }
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void loadOrder();
  }, [loadOrder, retryToken]);

  const handleCopyOrderId = async () => {
    if (!order) return;
    try {
      await navigator.clipboard.writeText(order.id);
      toast.success("ID do pedido copiado");
    } catch {
      toast.error("Não foi possível copiar o ID");
    }
  };

  const customerName = [order?.customer.firstName, order?.customer.lastName]
    .filter(Boolean)
    .join(" ");

  const showEntitlementMismatch = order?.entitlement.state === "Missing";

  return (
    <AdminPageLayout title="Detalhe do pedido">
      <main className="py-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Link
            to={backHref}
            className="inline-flex items-center text-sm text-blue-700 underline underline-offset-2 hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded-sm"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Voltar aos pedidos
          </Link>
          {!loading && !notFound && order && (
            <Button type="button" variant="outline" onClick={() => setRetryToken((v) => v + 1)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Atualizar
            </Button>
          )}
        </div>

        {loading && <DetailSkeleton />}

        {notFound && (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center space-y-4">
            <h1 className="text-xl font-semibold">Pedido não encontrado</h1>
            <p className="text-gray-600">
              Não existe pedido com o identificador informado.
            </p>
            <Button asChild variant="outline">
              <Link to="/admin/orders">Voltar aos pedidos</Link>
            </Button>
          </div>
        )}

        {!loading && !notFound && !order && error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900 space-y-3"
          >
            <p>{error}</p>
            <Button type="button" variant="outline" onClick={() => setRetryToken((v) => v + 1)}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!loading && !notFound && order && (
          <>
            {error && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900 space-y-3"
              >
                <p>{error}</p>
                <Button type="button" variant="outline" onClick={() => setRetryToken((v) => v + 1)}>
                  Tentar novamente
                </Button>
              </div>
            )}

            <header className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold">Pedido</h1>
                <Badge variant={getOrderStatusVariant(order.orderStatus)}>
                  Pedido: {getOrderStatusLabel(order.orderStatus)}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <code className="rounded bg-gray-100 px-2 py-1 text-sm font-mono break-all">
                  {order.id}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCopyOrderId}
                  aria-label="Copiar ID do pedido"
                >
                  <Copy className="mr-1 h-4 w-4" />
                  Copiar ID
                </Button>
              </div>
            </header>

            {showEntitlementMismatch && (
              <div
                role="alert"
                className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950"
              >
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="space-y-1 text-sm">
                  <p className="font-semibold">Divergência operacional detectada</p>
                  <p>
                    O pedido está marcado como pago, mas o acesso ao pacote não foi concedido
                    localmente. Revise pagamento, estudante e pacote abaixo — não há ação de
                    reparo automática nesta tela.
                  </p>
                </div>
              </div>
            )}

            <Section title="Identidade e ciclo de vida">
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field label="Criado em" value={formatOrderDateTime(order.createdAtUtc)} />
                <Field label="Atualizado em" value={formatOrderDateTime(order.updatedAtUtc)} />
                <Field label="Finalizado em" value={formatOrderDateTime(order.finishedAtUtc)} />
                <Field label="Cancelado em" value={formatOrderDateTime(order.cancelledAtUtc)} />
                <Field
                  label="Status do pedido"
                  value={
                    <Badge variant={getOrderStatusVariant(order.orderStatus)}>
                      {getOrderStatusLabel(order.orderStatus)}
                    </Badge>
                  }
                />
              </dl>
            </Section>

            <Section title="Valores, desconto e voucher">
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Moeda" value={order.currency} />
                <Field label="Subtotal" value={formatOrderCurrency(order.subtotal, order.currency)} />
                <Field
                  label="Desconto"
                  value={formatOrderCurrency(order.discountTotal, order.currency)}
                />
                <Field label="Total" value={formatOrderCurrency(order.total, order.currency)} />
                <Field label="Voucher" value={order.voucherCode ?? "—"} />
              </dl>

              {order.lines.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th scope="col" className="py-2 pr-3">Item</th>
                        <th scope="col" className="py-2 pr-3">Qtd</th>
                        <th scope="col" className="py-2 pr-3">Unitário</th>
                        <th scope="col" className="py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.lines.map((line) => (
                        <tr key={line.id} className="border-t border-gray-100">
                          <td className="py-2 pr-3">
                            {line.nameSnapshot ?? line.productId}
                            <span className="block text-xs text-gray-500">
                              Snapshot da compra
                            </span>
                          </td>
                          <td className="py-2 pr-3">{line.quantity}</td>
                          <td className="py-2 pr-3">
                            {formatOrderCurrency(line.unitPrice, order.currency)}
                          </td>
                          <td className="py-2">
                            {formatOrderCurrency(line.lineTotal, order.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            <Section title="Cliente, estudante e pacote">
              <dl className="grid gap-4 sm:grid-cols-2">
                <Field label="Cliente" value={customerName || "—"} />
                <Field label="E-mail" value={order.customer.email ?? "—"} />
                <Field label="Telefone" value={order.customer.phoneMasked ?? "—"} />
                <Field
                  label="Estudante"
                  value={
                    order.student ? (
                      <Link
                        to={`/admin/access/students/${order.student.id}`}
                        className="text-blue-700 underline underline-offset-2"
                      >
                        {order.student.name} ({order.student.email})
                      </Link>
                    ) : (
                      "Indisponível"
                    )
                  }
                />
                <Field
                  label="Pacote"
                  value={
                    order.package?.name ? (
                      order.package.catalogExists && order.package.id ? (
                        <Link
                          to={`/admin/packages/${order.package.id}`}
                          className="text-blue-700 underline underline-offset-2"
                        >
                          {order.package.name}
                        </Link>
                      ) : (
                        <>
                          {order.package.name}
                          {order.package.isSnapshot && (
                            <span className="block text-xs text-gray-500">
                              Snapshot da compra (catálogo pode ter mudado)
                            </span>
                          )}
                        </>
                      )
                    ) : (
                      "Indisponível"
                    )
                  }
                />
                <Field label="ID do pacote" value={order.package?.id ?? "—"} />
              </dl>
            </Section>

            <Section title="Pagamentos">
              <p className="text-sm text-gray-600">
                As informações do provedor refletem o último estado registrado localmente — não
                implicam consulta em tempo real ao PSP.
              </p>

              {order.payments.length === 0 ? (
                <p className="text-sm text-gray-700">Sem pagamento registrado.</p>
              ) : (
                <div className="space-y-4">
                  {order.payments.map((payment) => (
                    <article
                      key={payment.id}
                      className="rounded-md border border-gray-200 p-4 space-y-3"
                    >
                      <div className="flex flex-wrap gap-2">
                        <Badge variant={getPaymentStatusVariant(payment.status)}>
                          Pagamento: {getPaymentStatusLabel(payment.status)}
                        </Badge>
                        <Badge variant="outline">
                          Provedor: {getPaymentProviderLabel(payment.provider)}
                        </Badge>
                        <Badge variant="outline">
                          Método: {getPaymentMethodLabel(payment.method)}
                        </Badge>
                      </div>

                      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
                        <Field label="ID interno" value={payment.id} />
                        <Field
                          label="ID no provedor"
                          value={payment.providerPaymentId ?? "—"}
                        />
                        <Field
                          label="Valor"
                          value={formatOrderCurrency(payment.amount, payment.currency)}
                        />
                        <Field
                          label="Autorizado"
                          value={
                            payment.amountAuthorized != null
                              ? formatOrderCurrency(payment.amountAuthorized, payment.currency)
                              : "—"
                          }
                        />
                        <Field
                          label="Capturado"
                          value={
                            payment.amountCaptured != null
                              ? formatOrderCurrency(payment.amountCaptured, payment.currency)
                              : "—"
                          }
                        />
                        <Field
                          label="Reembolsado"
                          value={
                            payment.amountRefunded != null
                              ? formatOrderCurrency(payment.amountRefunded, payment.currency)
                              : "—"
                          }
                        />
                        <Field
                          label="Criado em"
                          value={formatOrderDateTime(payment.createdAtUtc)}
                        />
                        <Field
                          label="Autorizado em"
                          value={formatOrderDateTime(payment.authorizedAtUtc)}
                        />
                        <Field
                          label="Capturado em"
                          value={formatOrderDateTime(payment.capturedAtUtc)}
                        />
                        <Field
                          label="Falhou em"
                          value={formatOrderDateTime(payment.failedAtUtc)}
                        />
                        <Field
                          label="Cancelado em"
                          value={formatOrderDateTime(payment.cancelledAtUtc)}
                        />
                        <Field
                          label="Reembolsado em"
                          value={formatOrderDateTime(payment.refundedAtUtc)}
                        />
                        {(payment.failureCode || payment.failureMessage) && (
                          <>
                            <Field label="Código de falha" value={payment.failureCode ?? "—"} />
                            <Field
                              label="Mensagem de falha"
                              value={payment.failureMessage ?? "—"}
                            />
                          </>
                        )}
                      </dl>
                    </article>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Diagnóstico de acesso">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={getEntitlementStateVariant(order.entitlement.state)}>
                  Acesso: {getEntitlementStateLabel(order.entitlement.state)}
                </Badge>
              </div>
              <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                <Field
                  label="Estudante resolvido"
                  value={order.entitlement.studentResolved ? "Sim" : "Não"}
                />
                <Field
                  label="Pacote resolvido"
                  value={order.entitlement.packageResolved ? "Sim" : "Não"}
                />
                <Field
                  label="StudentPackage existe"
                  value={order.entitlement.studentPackageExists ? "Sim" : "Não"}
                />
                <Field label="ID estudante" value={order.entitlement.studentId ?? "—"} />
                <Field label="ID pacote" value={order.entitlement.packageId ?? "—"} />
              </dl>
              <p className="text-xs text-gray-500">
                Status de pagamento capturado não implica pedido pago nem acesso concedido.
                Cada eixo permanece independente.
              </p>
            </Section>
          </>
        )}
      </main>
    </AdminPageLayout>
  );
};

export default AdminOrderDetailPage;
